import React, { FC } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import ErrorMessageText from '../ErrorMessageText';
import { SFormGroup } from './styles';
import Label from '../Label';

export type OptionsProps = { value: number | string; label: string }[];
interface SelectMultiInputProps {
    control: any;
    name: string;
    label?: string;
    placeholder?: string;
    options: OptionsProps;
    required?: boolean;
    semi?: boolean;
    isDisabled?: boolean;
}

const getValues = (ids: (string | number)[], options: OptionsProps): OptionsProps | null => {
    if (!ids || !options) {
        return null;
    }
    return ids.map((id: string | number) => ({
        label: options.find((option) => option.value === id)?.label || '',
        value: id,
    }));
};

const SelectMultiInput: FC<SelectMultiInputProps> = ({
    control,
    label,
    placeholder,
    name,
    options,
    required = false,
    semi,
    isDisabled,
}) => {
    const { t } = useTranslation();
    const {
        formState: { errors },
        setValue,
    } = useFormContext();

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value, ...rest } }) => (
                <SFormGroup semi={semi} withError={!!errors[name]}>
                    {label && <Label name={name} required={required} text={label} />}
                    <Select
                        {...rest}
                        className="select"
                        classNamePrefix="react-select"
                        isDisabled={isDisabled}
                        onChange={(values) => {
                            if (!values.length) {
                                setValue(name, []);
                            }
                            onChange(values.map((val) => val.value));
                        }}
                        options={options}
                        placeholder={placeholder ?? t('common.select-field-placeholder')}
                        value={getValues(value, options)}
                        isClearable
                        isMulti
                    />
                    {errors[name] && (
                        <ErrorMessage
                            errors={errors}
                            name={name}
                            render={({ message }) => <ErrorMessageText text={message} />}
                        />
                    )}
                </SFormGroup>
            )}
            rules={{ required: required ? `${t('common.required-field')}` : false }}
        />
    );
};

export default SelectMultiInput;
